import React from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/system';
import { CHILDREN_PROP_TYPES } from 'propTypes';
import { getImageWithWidth, getSrcSet } from 'helpers/images';
import Cover from 'bento-ordering/menus/ui_components/Cover';
import muiPropForwarding from 'styles/helpers/muiPropForwarding';

const StyledHero = styled('div', {
  shouldForwardProp: muiPropForwarding('heroBackgroundColor', 'ownState')
})(({ theme, heroBackgroundColor, ownState }) => {
  const ownHeroStyles = ownState?.hero?.(theme) ?? {};

  return {
    height: 352,
    position: 'relative',
    backgroundColor: heroBackgroundColor,
    [theme.breakpoints.down('lg')]: {
      height: 260
    },
    [theme.breakpoints.down('sm')]: {
      height: 164
    },
    ...ownHeroStyles
  };
});

const HeroImage = styled('img', {
  shouldForwardProp: muiPropForwarding('focalPointX', 'focalPointY')
})(({ focalPointX, focalPointY }) => ({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  objectPosition:
    focalPointX && focalPointY
      ? `${focalPointX * 100}% ${focalPointY * 100}%`
      : 'initial'
}));

const FULL_WIDTH_SIZES = [640, 750, 900, 1536, 2048, 2560];

function Hero({ image, children, dataTestId, ...rest }) {
  const { heroBackgroundColor, CoverProps = {}, heroOwnState } = rest;
  const { ownState: coverOwnState = {} } = CoverProps;

  const resizedImage = getImageWithWidth(image.url, 600);
  const srcSet = getSrcSet(image.url, FULL_WIDTH_SIZES);

  return (
    <StyledHero
      heroBackgroundColor={heroBackgroundColor}
      data-testid={dataTestId}
      ownState={heroOwnState}
    >
      {image.url && (
        <>
          <Cover ownState={coverOwnState}>
            <HeroImage
              focalPointX={image.focal_point_x}
              focalPointY={image.focal_point_y}
              srcSet={srcSet}
              src={resizedImage}
              alt={image.alt_text}
              sizes="100vw"
            />
          </Cover>
        </>
      )}
      {children}
    </StyledHero>
  );
}

Hero.propTypes = {
  dataTestId: PropTypes.string,
  children: CHILDREN_PROP_TYPES,
  image: PropTypes.shape({
    url: PropTypes.string,
    alt_text: PropTypes.string,
    focal_point_x: PropTypes.number,
    focal_point_y: PropTypes.number
  })
};

Hero.defaultProps = {
  dataTestId: 'hero'
};

export default Hero;
